/**
 * Shopify Consent → GTM DataLayer Bridge
 *
 * Bridges Shopify's native consent banner to Google Tag Manager's dataLayer
 * using Google Consent Mode v2 format. This allows GTM tags (including Bing UET)
 * to respect user consent choices.
 *
 * Flow:
 * 1. Sets default consent state (denied) on page load
 * 2. Listens for Shopify consent events
 * 3. Updates dataLayer when consent changes
 * 4. Checks for existing consent on load
 */

(function() {
  'use strict';

  const DEBUG = false;
  const log = (...args) => DEBUG && console.log('[Consent Bridge]', ...args);

  // Initialize dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || [];

  /**
   * Push consent state to dataLayer in Google Consent Mode v2 format
   */
  function updateConsent(consentState) {
    log('Updating consent:', consentState);

    window.dataLayer.push({
      event: 'consent_update',
      consent_state: consentState
    });

    // Google Consent Mode v2 format
    window.dataLayer.push({
      event: 'gtm.consent',
      'gtm.start': new Date().getTime()
    });
  }

  /**
   * Map Shopify consent to Google Consent Mode categories
   */
  function mapShopifyConsent(shopifyConsent) {
    log('Mapping Shopify consent:', shopifyConsent);

    // Shopify consent categories:
    // - marketing: Marketing/advertising tracking
    // - analytics: Analytics tracking
    // - preferences: Preference cookies
    // - sale_of_data: Data selling (California)

    const consentMap = {
      ad_storage: shopifyConsent.marketing ? 'granted' : 'denied',
      ad_user_data: shopifyConsent.marketing ? 'granted' : 'denied',
      ad_personalization: shopifyConsent.marketing ? 'granted' : 'denied',
      analytics_storage: shopifyConsent.analytics ? 'granted' : 'denied',
      functionality_storage: 'granted', // Always granted for site functionality
      personalization_storage: shopifyConsent.preferences ? 'granted' : 'denied',
      security_storage: 'granted' // Always granted for security
    };

    return consentMap;
  }

  /**
   * Set default consent state (all denied) before banner interaction
   */
  function setDefaultConsent() {
    log('Setting default consent (denied)');

    const defaultConsent = {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
      functionality_storage: 'granted',
      personalization_storage: 'denied',
      security_storage: 'granted'
    };

    window.dataLayer.push({
      event: 'consent_default',
      consent_state: defaultConsent
    });

    // Google Consent Mode v2 default
    window.dataLayer.push({
      event: 'gtm.consent_default'
    });
  }

  /**
   * Read and push current consent from Shopify API
   */
  function syncCurrentConsent() {
    if (!window.Shopify || !window.Shopify.customerPrivacy) {
      log('Shopify.customerPrivacy not available yet');
      return false;
    }

    const trackingConsent = window.Shopify.customerPrivacy.getTrackingConsent();
    log('Current tracking consent:', trackingConsent);

    // Check if user has made a consent choice
    if (trackingConsent === 'no_interaction') {
      log('No consent interaction yet, using defaults');
      return false;
    }

    // Get detailed consent for each category
    const analytics = window.Shopify.customerPrivacy.analyticsProcessingAllowed();
    const marketing = window.Shopify.customerPrivacy.marketingAllowed();
    const preferences = window.Shopify.customerPrivacy.preferencesProcessingAllowed();
    const saleOfData = window.Shopify.customerPrivacy.saleOfDataAllowed();

    log('Detailed consent:', { analytics, marketing, preferences, saleOfData });

    const shopifyConsent = {
      analytics: analytics,
      marketing: marketing,
      preferences: preferences,
      sale_of_data: saleOfData
    };

    const consentMap = mapShopifyConsent(shopifyConsent);
    updateConsent(consentMap);

    return true;
  }

  /**
   * Listen for Shopify consent collection events
   */
  function setupConsentListeners() {
    // Method 1: Subscribe to Shopify's Customer Privacy API (recommended)
    if (window.Shopify && window.Shopify.customerPrivacy && window.Shopify.customerPrivacy.subscribe) {
      window.Shopify.customerPrivacy.subscribe('consentCollected', () => {
        log('consentCollected subscription fired');
        syncCurrentConsent();
      });

      window.Shopify.customerPrivacy.subscribe('consentChanged', () => {
        log('consentChanged subscription fired');
        syncCurrentConsent();
      });

      log('Shopify.customerPrivacy subscriptions set up');
    }

    // Method 2: Fallback event listener (backup)
    document.addEventListener('visitorConsentCollected', (event) => {
      log('visitorConsentCollected event:', event);

      // Small delay to ensure Shopify API is updated
      setTimeout(() => {
        syncCurrentConsent();
      }, 100);
    });

    log('Consent listeners set up');
  }

  /**
   * Wait for Shopify Customer Privacy API to be available
   */
  function waitForCustomerPrivacy(callback, maxAttempts = 50) {
    let attempts = 0;

    const check = () => {
      attempts++;

      if (window.Shopify && window.Shopify.customerPrivacy) {
        log('Shopify.customerPrivacy is available');
        callback();
        return;
      }

      if (attempts >= maxAttempts) {
        log('Shopify.customerPrivacy not available after', maxAttempts, 'attempts');
        return;
      }

      setTimeout(check, 100);
    };

    check();
  }

  /**
   * Initialize consent bridge
   */
  function init() {
    log('Initializing consent bridge');

    // Set default consent state immediately
    setDefaultConsent();

    // Set up listeners for consent changes
    setupConsentListeners();

    // Wait for Shopify API and sync current consent
    waitForCustomerPrivacy(() => {
      syncCurrentConsent();
    });
  }

  // Start immediately
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
